// ─────────────────────────────────────────────────────────────────────────────
// JISPMAN shared TypeScript types — mirrors Prisma schema + API DTOs
// No `any` without a justifying comment. Strict null checks enforced.
// ─────────────────────────────────────────────────────────────────────────────

// ── API envelope ──────────────────────────────────────────────────────────────

export interface ApiSuccess<T> {
  success: true
  data: T
}

export interface ApiPaginated<T> {
  success: true
  data: T[]
  meta: {
    total: number
    page: number
    perPage: number
    totalPages: number
  }
}

export interface ApiError {
  success: false
  error: { code: string; message: string; details?: unknown }
  meta?: { timestamp: string }
}

export type ApiResponse<T> = ApiSuccess<T> | ApiError
export type PaginatedResponse<T> = ApiPaginated<T> | ApiError

// ── Auth ──────────────────────────────────────────────────────────────────────

export interface AuthUser {
  id: string
  email: string
  name: string
  globalRole: string | null
  siteIds: string[]
  ownedSiteIds: string[]
  /** Map of siteId → site role — populated from JWT; used by router guards */
  siteRoles: Record<string, string>
}

export interface LoginResponse {
  accessToken: string
  refreshToken: string
  user: Pick<AuthUser, 'id' | 'email' | 'name' | 'globalRole'>
  /** Set when a recovery code was spent to get in, so the UI can say one is gone. */
  usedRecoveryCode?: boolean
}

/**
 * What `/auth/login` answers for an account with a second factor: a handle naming a pending
 * login, and nothing that can be used as a credential. The tokens only exist after
 * `/auth/login/2fa` accepts a code.
 */
export interface TwoFactorChallenge {
  twoFactorRequired: true
  challengeToken: string
  /** Seconds the handle stays valid. */
  expiresIn: number
  methods: string[]
  /** A masked address, so the person can tell which account is asking. */
  hint: { email: string }
}

/** `twoFactorRequired` is the discriminant; everything else differs between the two. */
export type LoginOutcome = LoginResponse | TwoFactorChallenge

export function isTwoFactorChallenge(v: LoginOutcome): v is TwoFactorChallenge {
  return (v as TwoFactorChallenge).twoFactorRequired === true
}

// ── Two-factor authentication ─────────────────────────────────────────────────

/** The actions a second factor can be demanded for. Mirrors the backend's key list. */
export type TwoFactorAction =
  | 'payout_destination'
  | 'withdrawal'
  | 'login'
  | 'twofa_admin'
  | 'role_grant'
  | 'gateway_credentials'
  | 'api_key_webhook'
  | 'account_identity'

export interface TwoFactorStatus {
  enabled: boolean
  method: string | null
  /** A secret exists but no code has confirmed it — the closed-tab case. */
  pending: boolean
  confirmedAt: string | null
  recoveryCodesRemaining: number
  recoveryCodesLow: boolean
  stepUp: {
    satisfied: boolean
    reason: 'not_enrolled' | 'factor_not_used' | 'stale' | null
    windowSeconds: number
  }
}

export interface TwoFactorEnrolment {
  secret: string
  uri: string
  recoveryCodes: string[]
  warning: string
}

/**
 * One row of the policy screen. The copy comes from the server so the row, the confirmation
 * dialog and the "switched off" email cannot drift apart.
 */
export interface TwoFactorPolicyRow {
  action: TwoFactorAction
  required: boolean
  /** Where the state came from, so the screen can say so rather than imply a choice. */
  source: 'required' | 'platform' | 'site' | 'default'
  switchable: boolean
  siteScoped: boolean
  editable: boolean
  label: string
  what: string
  on: string
  off: string
}

export interface TwoFactorPolicyResponse {
  siteId: string | null
  /** Without an enrolment every row below is inert — the screen must say so. */
  enrolled: boolean
  actions: TwoFactorPolicyRow[]
}

export interface RefreshResponse {
  accessToken: string
  refreshToken: string
}

// ── Geography ─────────────────────────────────────────────────────────────────

export interface Location {
  id: string
  name: string
  countryCode: string
  countryName: string
  region: string | null
  city: string | null
  address: string | null
  timezone: string
  currencyCode: string
  latitude: number | null
  longitude: number | null
  active: boolean
  ownerUserId: string
  createdAt: string
  updatedAt: string
}

export interface Country {
  code: string
  name: string
  timezone: string
  currency: string
}

// ── Sites ─────────────────────────────────────────────────────────────────────

export type ManagementMode = 'full' | 'partial'
export type SiteType = 'hotspot' | 'pppoe' | 'both'

export interface Site {
  id: string
  locationId: string
  ownerId: string
  name: string
  code: string
  type: SiteType
  managementMode: ManagementMode
  hotspotEnabled: boolean
  advertsEnabled: boolean
  /** Public PPPoE self-care page (/selfcare/<code>) and its owner-set branding. */
  selfcareEnabled?: boolean
  selfcareSlogan?: string | null
  selfcareOffice?: string | null
  selfcareLogoUrl?: string | null
  customerSupportPhone: string | null
  /** Shown to this site's OWN customers; falls back to the platform contact (ADR-077). */
  customerSupportEmail?: string | null
  /**
   * Site-local TV/IPTV server, surfaced as a portal link and opened in the hotspot's
   * walled garden. Site-scoped rather than global because a LAN address is only
   * reachable from that site's own network.
   */
  videoUrl: string | null
  currencyCode: string
  active: boolean
  createdAt: string
  updatedAt: string
  location?: Location
}

export type SiteRole = 'operator' | 'reseller' | 'customer_support' | 'technician'
export type GlobalRole = 'super_admin' | 'site_owner'
export type AnyRole = GlobalRole | SiteRole

export interface SiteUser {
  siteId: string
  userId: string
  siteRole: SiteRole
  createdAt: string
  user?: Pick<User, 'id' | 'name' | 'email' | 'active'>
}

// ── Users ─────────────────────────────────────────────────────────────────────

export interface User {
  id: string
  email: string
  name: string
  phone: string | null
  globalRole: GlobalRole | null
  active: boolean
  createdAt: string
  siteUsers?: SiteUser[]
}

// ── Devices ──────────────────────────────────────────────────────────────────

export type DeviceType = 'mikrotik_v6' | 'mikrotik_v7' | 'openwrt' | 'cisco' | 'ruijie' | 'linux'
export type PortProtocol = 'ssh' | 'winbox' | 'webfig' | 'routeros_api' | 'custom'

export interface Device {
  id: string
  siteId: string
  name: string
  type: DeviceType
  /** full = central RADIUS (default); partial = read-only + device-API vouchers. */
  managementMode?: 'full' | 'partial'
  managementIp: string | null
  serial: string | null
  mac: string | null
  /** Live facts discovered by polling (RouterOS API / SNMP) — not user-entered. */
  hardwareModel?: string | null
  firmwareVersion?: string | null
  factsUpdatedAt?: string | null
  /**
   * When we last actually heard from this device — the newest poll sample where it answered.
   *
   * Deliberately NOT the newest sample's timestamp: the poller records one on every pass,
   * including passes where the device was unreachable, so that is "when we last looked".
   * Null means it has never once answered, which is a different problem from "a while ago".
   */
  lastSeenAt?: string | null
  /** When the poller last TRIED, answered or not. Detail endpoint only. */
  lastPolledAt?: string | null
  /** Whether the most recent pass found it up. Detail endpoint only. */
  onlineNow?: boolean | null
  enabled: boolean
  imageUrl: string | null
  notes: string | null
  createdAt: string
  updatedAt: string
  vpnCredential?: VpnCredential | null
  portAllocations?: DevicePortAllocation[]
  tr069Config?: DeviceTr069Config | null
  deviceMetrics?: DeviceMetric[]
  /**
   * Resolved VPN transport for this device (ADR-037). Derived on the backend from
   * `type` + the assigned VPN server's capabilities: mikrotik_v7/openwrt/linux →
   * wireguard, mikrotik_v6 → sstp (fallback l2tp), ruijie/cisco → per capability.
   * Optional — additive; older payloads may omit it.
   */
  vpnProtocol?: VpnProtocol | null
  /** Other transports the hub would also accept for this device (ADR-037). */
  vpnFallbacks?: VpnProtocol[]
  /**
   * What this device can actually report, resolved on the backend from its type and
   * management mode (backend `shared/deviceCapabilities.ts`). Drives which detail
   * tabs are usable: MikroTik speaks the RouterOS API, everything else is polled over
   * SNMP/SSH which exposes far less. Optional — additive; when absent the UI must
   * assume everything is supported rather than hiding working tabs.
   */
  capabilities?: DeviceCapabilityReport | null
}

/** Per-device resolved VPN transport (ADR-037). */
export type VpnProtocol = 'wireguard' | 'sstp' | 'l2tp'

/** Mirrors the backend `DeviceCapability` union — keep the two in step. */
export type DeviceCapability =
  | 'neighbours' | 'dhcpLeases' | 'queues' | 'deviceVouchers' | 'configBackup'
  | 'rosConsole' | 'sshConsole' | 'interfaces' | 'metrics' | 'deviceSessionCount'
  | 'temperature' | 'diskUsage'

export interface DeviceCapabilityReport {
  metricsSource: 'routeros' | 'snmp' | 'none'
  supported: Record<DeviceCapability, boolean>
  /** Operator-facing explanation, present only for unsupported capabilities. */
  reason: Partial<Record<DeviceCapability, string>>
}

export interface VpnCredential {
  id: string
  deviceId: string
  vpnServerId: string
  innerIp: string
  wgPublicKey: string | null
  configPayload: string | null
  active: boolean
  createdAt: string
  updatedAt: string
  /** The hub this peer belongs to — publicIp/port drive the real WG Endpoint line. */
  vpnServer?: {
    id: string
    name: string
    publicIp?: string | null
    port?: number | null
    supportsWireguard?: boolean
    supportsSstp?: boolean
    supportsL2tp?: boolean
  } | null
}

export interface DevicePortAllocation {
  id: string
  deviceId: string
  vpnServerId: string
  protocol: PortProtocol
  label: string | null
  internalPort: number
  publicPort: number
  natRuleActive: boolean
  active: boolean
  createdAt: string
}

export interface DeviceTr069Config {
  id: string
  deviceId: string
  acsDeviceId: string | null
  cwmpUsername: string | null
  connectionRequestUrl: string | null
  informIntervalSeconds: number
  lastInformAt: string | null
  lastParameterSnapshot: Record<string, unknown> | null
  createdAt: string
  updatedAt: string
}

export interface DeviceMetric {
  id: string
  deviceId: string
  online: boolean
  cpuLoad: number | null
  memoryUsed: number | null
  diskUsed: number | null
  activeSessions: number | null
  uptimeSeconds: number | null
  temperature: number | null
  recordedAt: string
}

export interface DeviceInterface {
  id: string
  deviceId: string
  name: string
  type: string | null
  mac: string | null
  mtu: number | null
  running: boolean
  disabled: boolean
  lastSeenAt: string | null
  stats?: DeviceInterfaceStat[]
}

export interface DeviceInterfaceStat {
  id: string
  interfaceId: string
  rxBps: number | null
  txBps: number | null
  rxBytes: number | null
  txBytes: number | null
  recordedAt: string
}

// ── Services & Packages ───────────────────────────────────────────────────────

export interface Service {
  id: string
  name: string
  slug: string
  description: string | null
  active: boolean
  createdAt: string
  packages?: ServicePackage[]
}

export interface ServicePackage {
  id: string
  serviceId: string
  siteId: string | null
  name: string
  slug: string
  price: number
  currency: string
  validityMinutes: number | null
  limits: Record<string, unknown> | null
  active: boolean
  createdAt: string
  service?: Pick<Service, 'id' | 'name' | 'slug'>
}

// ── Vouchers ──────────────────────────────────────────────────────────────────

export type VoucherSource = 'jispman_radius' | 'local_import'
export type VoucherStatus = 'available' | 'sold' | 'used' | 'expired'

export interface Voucher {
  id: string
  siteId: string
  packageId: string
  code: string
  username: string | null
  source: VoucherSource
  status: VoucherStatus
  costPrice: number | null
  sellingPrice: number | null
  currency: string
  customerId: string | null
  soldAt: string | null
  usedAt: string | null
  expiresAt: string | null
  createdAt: string
  updatedAt: string
  package?: Pick<ServicePackage, 'id' | 'name' | 'currency'> & { service?: Pick<Service, 'name'> }
}

export interface VoucherImportBatch {
  id: string
  siteId: string
  importedBy: string
  filename: string
  totalCount: number
  importedCount: number
  duplicateCount: number
  failedCount: number
  status: 'pending' | 'processing' | 'done' | 'failed'
  errorLog: unknown[] | null
  createdAt: string
  completedAt: string | null
}

// ── Billing ───────────────────────────────────────────────────────────────────

export interface Wallet {
  id: string
  ownerType: 'site' | 'user' | 'customer'
  ownerId: string
  walletType: 'money' | 'sms' | 'sms_credits'
  balance: number
  reserveMinimum: number
  currency: string
  createdAt: string
  updatedAt: string
}

export type PaymentStatus = 'pending' | 'completed' | 'failed'

export interface PaymentIntent {
  id: string
  siteId: string
  customerPhone: string
  amount: number
  currency: string
  status: PaymentStatus
  providerId: string | null
  reference: string
  activationToken: string | null
  createdAt: string
  updatedAt: string
}

export interface MobileMoneyTransaction {
  id: string
  paymentIntentId: string
  externalId: string | null
  provider: string
  amount: number
  currency: string
  status: 'pending' | 'completed' | 'failed'
  payerPhone: string | null
  processedAt: string | null
  createdAt: string
  paymentIntent?: PaymentIntent
}

export interface LedgerEntry {
  id: string
  accountType: string
  accountId: string
  entryType: 'debit' | 'credit'
  amount: number
  currency: string
  reference: string
  confirmed: boolean
  metadata: unknown
  createdAt: string
}

export interface PaymentProvider {
  id: string
  name: string
  driver: string
  countryCodes: string[]
  active: boolean
  createdAt: string
}

// ── Customer Intelligence (CIP) ───────────────────────────────────────────────

export type CustomerTier =
  | 'Explorer'
  | 'Returning'
  | 'Member'
  | 'Preferred'
  | 'Gold'
  | 'Platinum VIP'

export type HealthCategory = 'Healthy' | 'Active' | 'At Risk' | 'High Risk' | 'Churn Imminent'

export interface Customer {
  id: string
  jspotId: string
  siteId: string
  phone: string | null
  countryCode: string | null
  name: string | null
  email: string | null
  tier: CustomerTier
  rewardPoints: number
  chestKeys: number
  walletBalance: number
  walletCurrency: string
  totalPurchases: number
  totalSpend: number
  lifetimeValue: number
  churnRiskScore: number
  healthScore: number
  lastSeenAt: string | null
  createdAt: string
  updatedAt: string
}

export interface CustomerTierEvent {
  id: string
  customerId: string
  oldTier: CustomerTier
  newTier: CustomerTier
  triggeredAt: string
}

export interface RewardTransaction {
  id: string
  customerId: string
  type: 'points_earned' | 'points_redeemed' | 'bonus_data' | 'cashback' | 'spin_win' | 'chest_open'
  amount: number
  reference: string | null
  createdAt: string
}

export interface SpinResult {
  id: string
  customerId: string
  outcome: '100mb' | '250mb' | '500mb' | '1gb' | '1h_unlimited' | 'double_points' | 'double_data'
  rarity: 'common' | 'uncommon' | 'rare' | 'legendary'
  createdAt: string
}

// ── Advertising (ADR-017) ─────────────────────────────────────────────────────

export type AdType = 'image' | 'video' | 'audio'

/** Ad lifecycle status machine (ADR-035 §1). */
export type AdStatus = 'draft' | 'scheduled' | 'live' | 'paused' | 'ended'

/** Monetization campaign type (ADR-035 §4). */
export type AdCampaignType = 'cpm' | 'cpv' | 'sponsored'

/** Optional audience segment for an assignment (ADR-035 §2). */
export type AdAudience = 'all' | 'new' | 'returning' | 'hotspot' | 'pppoe'

export interface Ad {
  id: string
  siteId: string
  title: string
  type: AdType
  fileUrl: string
  thumbnailUrl: string | null
  durationSeconds: number
  clickUrl: string | null
  advertiserName: string | null
  priority: number
  active: boolean
  startsAt: string | null
  endsAt: string | null
  createdAt: string
  updatedAt: string
  // ── ADR-035 monetization/lifecycle fields (additive; optional until the
  //    backend surfaces them on every payload). ────────────────────────────────
  status?: AdStatus
  campaignType?: AdCampaignType
  rateCents?: number
  currency?: string
}

/** Per-site/audience placement of an ad (ADR-035 §2). */
export interface AdAssignment {
  id: string
  adId: string
  siteId: string
  audience: AdAudience | null
  weight: number
  enabled: boolean
  createdAt: string
}

/** Impression/click rollup for an ad's detail surface (ADR-035 §3). */
export interface AdImpressionSummary {
  /** Completed views — the billable event, and the only one the advertiser's
   *  wallet is debited for. */
  total: number
  /** Opened but abandoned before the end. Costs the advertiser nothing. */
  partial: number
  last7d: number
  clicks: number
  ctr: number
}

/**
 * A captive portal blocks the wider internet before login, so an advert hosted
 * anywhere but the portal cannot load and the campaign fails silently. The API
 * returns these on create/update/detail so the operator hears it at upload time.
 */
export interface AdMediaWarning {
  field: 'fileUrl' | 'thumbnailUrl'
  url: string
  host: string
  code: 'offsite_asset'
  message: string
}

/** GET /ads/:id detail payload. */
export interface AdDetail {
  ad: Ad
  assignments: AdAssignment[]
  impressionSummary: AdImpressionSummary
}

export interface AdSurfConfig {
  id: string
  siteId: string
  enabled: boolean
  adsRequired: number
  freeSurfMinutes: number
  cooldownMinutes: number
  maxWatchesPerDay: number
  createdAt: string
  updatedAt: string
}

export interface AdSurfSession {
  id: string
  siteId: string
  customerId: string | null
  deviceMac: string
  adsWatched: number
  radiusUsername: string
  sessionExpiresAt: string
  completed: boolean
  createdAt: string
}

// ── Portal Config (ADR-017) ───────────────────────────────────────────────────

export type PortalLayout = 'single_card' | 'dual_card' | 'full_width'
export type BackgroundType = 'gradient' | 'image' | 'color'

export interface PortalConfig {
  id: string
  siteId: string
  logoUrl: string | null
  backgroundUrl: string | null
  backgroundType: BackgroundType
  backgroundValue: string | null
  primaryColor: string
  secondaryColor: string
  accentColor: string
  brandName: string | null
  tagline: string | null
  supportPhone: string | null
  layout: PortalLayout
  showAdsPanel: boolean
  showWatchToSurf: boolean
  showPackages: boolean
  showJspotLogin: boolean
  customCss: string | null
  createdAt: string
  updatedAt: string
}

// ── Multi-Portal Captive Portal (ADR-036) ──────────────────────────────────────

/** Target NAS/device flavour a portal is generated + deployed for. */
export type TargetDevice = 'mikrotik_v6' | 'mikrotik_v7' | 'openwrt' | 'ruijie' | 'generic'

export type CaptivePortalStatus = 'draft' | 'published'

/** Where the ad slot renders relative to the login/voucher card. */
export type AdSlotPosition = 'side' | 'below' | 'above' | 'overlay'

export interface PortalAdSlotConfig {
  enabled: boolean
  position: AdSlotPosition
  /** Optional CTA copy for a watch-to-surf ad slot. */
  ctaLabel?: string | null
}

/** Branding + layout + ad-slot payload persisted on `CaptivePortal.config`. */
export interface CaptivePortalConfigPayload {
  logoUrl: string | null
  primaryColor: string
  accentColor: string
  headline: string | null
  footer: string | null
  supportPhone?: string | null
  templateKey: string
  adSlot: PortalAdSlotConfig
}

export interface CaptivePortal {
  id: string
  siteId: string
  name: string
  templateKey: string
  status: CaptivePortalStatus
  isTemplate: boolean
  config: CaptivePortalConfigPayload
  targetDevice: TargetDevice | null
  createdAt: string
  updatedAt: string
}

/** A built-in template variant (config preset) returned by GET /portal/templates. */
export interface PortalTemplate {
  templateKey: string
  name: string
  description: string | null
  /** Whether this preset ships an enabled ad slot by default. */
  hasAdSlot: boolean
  config: CaptivePortalConfigPayload
}

/** Response of GET /portal/portals/:id/config?device= — a generated device bundle. */
export interface PortalDeviceConfig {
  device: TargetDevice
  filename: string
  contentType: string
  content: string
}

/** Response of POST /portal/portals/:id/deploy. */
export interface PortalDeployResult {
  queued: boolean
  /** Copy-paste config returned when the device is not reachable (queued=false). */
  config?: PortalDeviceConfig | null
  message?: string | null
}

// ── Notifications ─────────────────────────────────────────────────────────────

export interface NotificationEvent {
  id: string
  slug: string
  name: string
  category: string | null
  isSystem: boolean
  active: boolean
}

export interface NotificationTemplate {
  id: string
  siteId: string
  eventSlug: string
  channel: 'sms' | 'email' | 'whatsapp'
  template: string
  active: boolean
  createdAt: string
  updatedAt: string
}

// ── SMS ───────────────────────────────────────────────────────────────────────

export interface SmsMessage {
  id: string
  userId: string
  toNumber: string
  message: string
  segments: number
  costUgx: number | null
  status: 'pending' | 'sent' | 'failed'
  sentAt: string | null
  createdAt: string
}

// ── VPN ───────────────────────────────────────────────────────────────────────

export interface VpnServer {
  id: string
  name: string
  hostname: string
  publicIp: string
  port: number
  type: 'wireguard' | 'sstp'
  supportsWireguard: boolean
  supportsSstp: boolean
  /** L2TP/IPsec capability (ADR-037). Optional — additive; may be absent on old payloads. */
  supportsL2tp?: boolean
  /**
   * The hub's own WireGuard public key, needed to build a client .conf.
   * Null until the hub has been provisioned and `wg show wg0 public-key` recorded.
   */
  wgHubPublicKey: string | null
  /** Selection weight when allocating a device to a hub. Defaults to 1 server-side. */
  weight: number
  /** TR-069/CWMP endpoints — returned by GET /vpn-servers/:id only, not by the list. */
  tr069AcsUrl?: string | null
  tr069NbiUrl?: string | null
  region: string | null
  active: boolean
  createdAt: string
}

// ── TR-069 / CWMP management surface (ADR-018 / ADR-037) ────────────────────────

/** GET /devices/:id/tr069 — ACS/CWMP settings for a device (secrets never returned). */
/** GET /devices/:id/tr069 — the DeviceTr069Config row minus the encrypted password columns. */
export interface DeviceTr069Settings {
  acsDeviceId?: string | null
  cwmpUsername?: string | null
  connectionRequestUrl?: string | null
  connectionRequestUsername?: string | null
  informIntervalSeconds?: number | null
  lastInformAt?: string | null
}

/** PUT /devices/:id/tr069 payload. Passwords are write-only; omit to leave unchanged. */
export interface DeviceTr069Update {
  acsDeviceId?: string
  cwmpUsername?: string
  cwmpPassword?: string
  connectionRequestUrl?: string
  connectionRequestUsername?: string
  connectionRequestPassword?: string
  informIntervalSeconds?: number
}

/** Task types enqueued via POST /devices/:id/tr069/task (ADR-037). */
export type Tr069TaskType = 'refresh' | 'reboot' | 'firmware' | 'push'

// ── Socket.io events ──────────────────────────────────────────────────────────

export interface DeviceMetricEvent {
  deviceId: string
  metric: DeviceMetric
}

export interface DeviceStatusEvent {
  deviceId: string
  online: boolean
  recordedAt: string
}

// ── Utility types ─────────────────────────────────────────────────────────────

export type Tier = CustomerTier

export function getHealthCategory(score: number): HealthCategory {
  if (score >= 80) return 'Healthy'
  if (score >= 60) return 'Active'
  if (score >= 40) return 'At Risk'
  if (score >= 20) return 'High Risk'
  return 'Churn Imminent'
}

export function getTierColor(tier: CustomerTier): string {
  const colors: Record<CustomerTier, string> = {
    'Explorer': 'bg-slate-700 text-slate-300',
    'Returning': 'bg-blue-900 text-blue-300',
    'Member': 'bg-green-900 text-green-300',
    'Preferred': 'bg-amber-900 text-amber-300',
    'Gold': 'bg-yellow-900 text-yellow-300',
    'Platinum VIP': 'bg-purple-900 text-purple-300',
  }
  return colors[tier] ?? 'bg-slate-700 text-slate-300'
}

export function getHealthColor(score: number): string {
  if (score >= 80) return 'text-green-400'
  if (score >= 60) return 'text-blue-400'
  if (score >= 40) return 'text-amber-400'
  if (score >= 20) return 'text-orange-400'
  return 'text-red-400'
}

// ── Terminal (ADR-038) ─────────────────────────────────────────────────────────
export type TerminalMode = 'ssh' | 'ros'
export type TerminalEndReason = 'user_close' | 'idle_timeout' | 'error' | 'auth_fail'

export interface TerminalSession {
  id: string
  deviceId: string
  userId: string
  siteId: string
  mode: TerminalMode
  startedAt: string
  endedAt: string | null
  durationSeconds: number | null
  endReason: TerminalEndReason | null
  clientIp: string
  user?: { name: string }
}

// ── VPN Agent (ADR-040 / INFRA-005) ──────────────────────────────────────────
// Mirrors INFRA-005 §3 response shapes. All bytes are raw integers (not pre-formatted).
// Timestamps are Unix epoch seconds. null = unknown / not yet seen.

export interface VpnAgentHostMemory {
  totalBytes: number
  usedBytes: number
  usedPercent: number
}

export interface VpnAgentHostDisk {
  totalBytes: number
  usedBytes: number
  usedPercent: number
}

export interface VpnAgentHostServices {
  softether: boolean
  wireguard: boolean
  dhcpd: boolean
  mongod: boolean
  genieacs: boolean
  agent: boolean
}

export interface VpnAgentHost {
  hostname: string
  uptimeSeconds: number
  loadAvg: number[]
  cpuCount: number
  memory: VpnAgentHostMemory
  disk: VpnAgentHostDisk
  services: VpnAgentHostServices
}

export interface VpnAgentWgSummary {
  interface: string
  listenPort: number
  subnet: string
  peersTotal: number
  peersOnline: number
  rxBytes: number
  txBytes: number
}

export interface VpnAgentWgPeer {
  publicKey: string
  name: string
  innerIp: string
  endpoint: string | null
  latestHandshake: number | null
  lastHandshakeAgo: number | null   // seconds
  online: boolean
  rxBytes: number
  txBytes: number
  // enriched by the backend from vpn_credentials:
  deviceId: string | null
  deviceName: string | null
  siteId: string | null
  siteName: string | null
  siteCode: string | null
}

export interface VpnAgentSoftEtherHub {
  name: string
  status: string
  users: number
  sessions: number
  transferBytes: number
}

export interface VpnAgentSoftEtherStatus {
  version: string
  uptimeSeconds: number
  sessions: number
  users: number
  groups: number
  traffic: { outBytes: number; inBytes: number }
  hubs: VpnAgentSoftEtherHub[]
}

export interface VpnAgentSoftEtherUser {
  username: string
  fullName: string
  group: string
  authMethod: string
  numLogins: number
  lastLogin: string
  expiration: string
  transferBytes: number
}

export interface VpnAgentSoftEtherSession {
  name: string
  username: string
  clientIp: string
  clientHostname: string
  isSystem: boolean
  uniqueId: string
  createdAt: string
  lastCommAt: string
}

export interface VpnAgentOverview {
  host: VpnAgentHost
  wireguard: VpnAgentWgSummary
  softether: VpnAgentSoftEtherStatus
  generatedAt: number
}

export interface VpnAgentWgProvisionResult {
  publicKey: string
  innerIp: string
  name: string
  serverPublicKey: string
  endpoint: string
  allowedIps: string
  persistentKeepalive: number
  privateKey?: string     // only present when agent generated keypair — show ONCE
  clientConfig?: string   // ready-to-paste .conf — show ONCE
}

// ─────────────────────────────────────────────────────────────────────────────
// C1.2 — PPPoE self-care (customer-facing). Mirrors the payload shapes in
// backend/src/modules/customer-portal/customer-portal.routes.ts exactly. See
// ADR-060 (a PPPoE payment buys a term, not a month).
// ─────────────────────────────────────────────────────────────────────────────

/**
 * The public site header, from `GET /portal/:siteCode` with
 * `Accept: application/json`. The OTP endpoints take a site *id*, but the URL a
 * customer is handed carries the site *code*, so this is the lookup that turns
 * one into the other. It is deliberately the only pre-auth call the page makes.
 */
export interface SelfCareSite {
  id: string
  name: string
  code: string
  managementMode?: string
  currencyCode: string | null
  customerSupportPhone: string | null
  /** Shown to this site's OWN customers; falls back to the platform contact (ADR-077). */
  customerSupportEmail?: string | null
}

/**
 * One rung of the ADR-060 duration ladder, priced pro-rata by the API.
 * Never compute these client-side: the price a customer is shown must be the
 * price the server will charge, and `POST /pppoe/pay` re-derives it from `days`.
 */
export interface PppoeTermOption {
  days: number
  label: string
  amount: number
  currency: string
}

/** A settled payment against the subscription, newest first (max 6). */
export interface PppoeSelfCarePayment {
  /** Prisma Decimal — serialises as a string over JSON, so coerce before maths. */
  amount: string | number
  currency: string
  paidAt: string
  /** What the payment bought: service is paid up to here. */
  periodEnd: string
}

/** `GET /api/customer-portal/pppoe/account` (portal JWT required). */
export interface PppoeSelfCareAccount {
  /** Nullable until the C1.2 backfill reaches every legacy subscription. */
  accountNumber: string | null
  username: string
  packageName: string
  /** 'active' | 'suspended' — a terminated subscription is never returned. */
  status: string
  suspended: boolean
  renewsAt: string
  daysLeft: number
  expired: boolean
  monthlyPrice: number
  currency: string
  options: PppoeTermOption[]
  recentPayments: PppoeSelfCarePayment[]
}

/** `POST /api/customer-portal/pppoe/pay` → the mobile-money prompt was raised. */
export interface PppoePayIntent {
  paymentIntentId: string
  amount: number
  currency: string
  term: string
  status: string
}

/** `GET /portal/:siteCode/payment-status/:intentId` — public poll (B7). */
export interface PortalPaymentStatus {
  status: string
  message: string
}
